import { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../ui/table';
import { Badge } from '../ui/badge';
import LoadingRing from '../ui-components/LoadingRing';
import { getSyncRuns, SyncRun } from '../../services/api/syncedSystemsService';

const formatDate = (value?: string) =>
    value ? new Date(value).toLocaleString() : '—';

const manufacturerOutcomeVariant = (
    outcome: SyncRun['manufacturers'][number]['outcome']
) => (outcome === 'conflict' ? 'destructive' : 'secondary');

const isRevokeRun = (run: SyncRun) => run.kind === 'revoke';

function RunKindBadge({ run }: { run: SyncRun }) {
    return isRevokeRun(run) ? (
        <Badge variant="destructive">revoke</Badge>
    ) : (
        <Badge variant="outline">sync</Badge>
    );
}

function RunVerifiedBadge({ run }: { run: SyncRun }) {
    if (run.status !== 'ok') return <span>—</span>;
    if (!run.verification) return <Badge variant="outline">n/a</Badge>;
    return run.verification.ok ? (
        <Badge variant="outline">verified ✓</Badge>
    ) : (
        <Badge variant="destructive">failed ✗</Badge>
    );
}

function SyncRunDetail({ run }: { run: SyncRun }) {
    return (
        <div className="mt-2 mb-4 grid gap-4 md:grid-cols-2">
            <div>
                <h3 className="text-lg font-semibold mb-1">
                    Implants
                    <span className="ml-2 text-sm font-normal text-muted-foreground">
                        {run.systems.length}{' '}
                        {isRevokeRun(run) ? 'revoked' : 'synced'}
                    </span>
                </h3>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>Versions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {run.systems.map((system) => (
                            <TableRow key={system.systemId}>
                                <TableCell className="font-medium">
                                    {system.name}
                                </TableCell>
                                <TableCell>
                                    {system.versions.length > 0
                                        ? system.versions
                                              .map((version) => `v${version}`)
                                              .join(', ')
                                        : '—'}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
            {isRevokeRun(run) ? (
                <p className="text-sm text-muted-foreground self-start">
                    Revoked implants are hidden from the picker and reporting
                    until the data pipeline syncs them again.
                </p>
            ) : (
                <div>
                    <h3 className="text-lg font-semibold mb-1">
                        Manufacturers
                        <span className="ml-2 text-sm font-normal text-muted-foreground">
                            {run.manufacturers.length} synced
                        </span>
                    </h3>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>Outcome</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {run.manufacturers.map((manufacturer) => (
                                <TableRow key={manufacturer.manufacturerId}>
                                    <TableCell className="font-medium">
                                        {manufacturer.name}
                                    </TableCell>
                                    <TableCell>
                                        <Badge
                                            variant={manufacturerOutcomeVariant(
                                                manufacturer.outcome
                                            )}
                                        >
                                            {manufacturer.outcome}
                                        </Badge>
                                        {manufacturer.detail && (
                                            <span className="ml-2 text-sm text-muted-foreground">
                                                {manufacturer.detail}
                                            </span>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}
            {run.error && (
                <p className="text-sm text-destructive md:col-span-2">
                    {run.error}
                </p>
            )}
            {run.verification && !run.verification.ok && (
                <div className="md:col-span-2 text-sm text-destructive">
                    <p className="font-medium">Verification failures:</p>
                    <ul className="list-disc ml-5">
                        {run.verification.failures.map((failure, index) => (
                            <li key={index}>
                                [{failure.kind}] {failure.name ?? failure.id}:{' '}
                                {failure.detail}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

export default function SyncVerificationPage() {
    const [isLoading, setIsLoading] = useState(true);
    const [runs, setRuns] = useState<SyncRun[]>([]);
    const [expandedRunIndex, setExpandedRunIndex] = useState<number | null>(
        null
    );

    const fetchRuns = useCallback(async () => {
        setIsLoading(true);
        try {
            const loadedRuns = await getSyncRuns();
            setRuns(Array.isArray(loadedRuns) ? loadedRuns : []);
        } catch (error) {
            console.error('Failed to fetch sync runs:', error);
            setRuns([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchRuns();
    }, [fetchRuns]);

    const toggleExpanded = (index: number) =>
        setExpandedRunIndex((current) => (current === index ? null : index));

    if (isLoading) {
        return (
            <div className="flex justify-center p-12">
                <LoadingRing size="large" />
            </div>
        );
    }

    return (
        <div className="p-6 max-w-5xl mx-auto">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold">Active Sync Verification</h2>
            </div>
            {runs.length === 0 ? (
                <p className="text-muted-foreground">
                    No syncs recorded yet. Push from the data pipeline, then
                    reload this page.
                </p>
            ) : (
                <>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-8" />
                                <TableHead>Date</TableHead>
                                <TableHead>Kind</TableHead>
                                <TableHead>By</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Verified</TableHead>
                                <TableHead>Implants</TableHead>
                                <TableHead>Manufacturers</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {runs.map((run, index) => (
                                <TableRow
                                    key={index}
                                    className="cursor-pointer"
                                    data-state={
                                        index === expandedRunIndex
                                            ? 'selected'
                                            : undefined
                                    }
                                    onClick={() => toggleExpanded(index)}
                                >
                                    <TableCell>
                                        {index === expandedRunIndex ? (
                                            <ChevronDown className="h-4 w-4" />
                                        ) : (
                                            <ChevronRight className="h-4 w-4" />
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {formatDate(run.occurredAt)}
                                    </TableCell>
                                    <TableCell>
                                        <RunKindBadge run={run} />
                                    </TableCell>
                                    <TableCell className="font-medium">
                                        {run.actorName}
                                    </TableCell>
                                    <TableCell>
                                        <Badge
                                            variant={
                                                run.status === 'ok'
                                                    ? 'secondary'
                                                    : 'destructive'
                                            }
                                        >
                                            {run.status}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        <RunVerifiedBadge run={run} />
                                    </TableCell>
                                    <TableCell>{run.systems.length}</TableCell>
                                    <TableCell>
                                        {run.manufacturers.length}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {expandedRunIndex !== null && runs[expandedRunIndex] && (
                        <SyncRunDetail run={runs[expandedRunIndex]} />
                    )}
                </>
            )}
        </div>
    );
}
